// OneAmerica AssetCare Application Sections
// Form ICC18 I-31139

import { FundingPolicyType } from '@shared';

// Type for the sections object
type SectionsObject = {
  [key: string]: PolicyFormSection;
};

// Export sections as an object with camelCase keys matching their IDs
export const sharedOneAmericaSections: SectionsObject = {
  /* 1 ▸ First Proposed Insured --------------------------------------------------- */
  firstProposedInsured: {
    id: 'first-proposed-insured',
    title: 'First Proposed Insured',
    subsections: [
      {
        id: 'personal-info',
        title: 'Personal Information',
        questionGroups: [
          {
            id: 'name-info',
            format: 'grid',
            questions: [
              'firstProposedInsuredFirstName',
              'firstProposedInsuredMiddleInitial',
              'firstProposedInsuredLastName',
            ],
          },
          {
            id: 'address-info',
            format: 'default',
            questions: ['firstProposedInsuredStreetAddress', 'firstProposedInsuredCityStateZip'],
          },
          {
            id: 'birth-identity',
            format: 'grid',
            questions: [
              'firstProposedInsuredBirthDate',
              'firstProposedInsuredSsn',
              'firstProposedInsuredPlaceOfBirth',
            ],
          },
          {
            id: 'demographics',
            format: 'grid',
            questions: [
              'firstProposedInsuredGender',
              'firstProposedInsuredMarriedStatus',
              'firstProposedInsuredUsCitizen',
            ],
          },
          {
            id: 'non-us-citizen-info',
            format: 'default',
            questions: ['firstProposedNotUsCitizenDetails'],
          },
          {
            id: 'contact-info',
            format: 'grid',
            questions: ['firstProposedInsuredPhoneNumber', 'firstProposedInsuredEmailAddress'],
          },
          {
            id: 'employment',
            format: 'grid',
            questions: ['firstProposedInsuredOccupation', 'firstProposedInsuredEmployerName'],
          },
          {
            id: 'hidden-fields-for-signature-first',
            format: 'grid',
            hidden: true,
            questions: [
              'firstProposedInsuredFullName',
              'signedAtCity',
              'signedAtState',
              'signedAtCityState',
            ],
          },
        ],
      },
    ],
  },

  /* 2 ▸ Second Proposed Insured --------------------------------------------------- */
  secondProposedInsured: {
    id: 'second-proposed-insured',
    title: 'Second Proposed Insured',
    conditional: {
      visible: {
        field: 'fundingPolicyType',
        operator: 'equals' as const,
        value: FundingPolicyType.jointAssetCare,
      },
    },
    subsections: [
      {
        id: 'second-proposed-insured-personal-info',
        title: 'Personal Information',
        questionGroups: [
          {
            id: 'name-info',
            format: 'grid',
            questions: [
              'secondProposedInsuredFirstName',
              'secondProposedInsuredMiddleInitial',
              'secondProposedInsuredLastName',
            ],
          },
          {
            id: 'relationship',
            format: 'default',
            questions: ['secondProposedInsuredRelationship'],
          },
          {
            id: 'address-info',
            format: 'default',
            questions: ['secondProposedInsuredStreetAddress', 'secondProposedInsuredCityStateZip'],
          },
          {
            id: 'birth-identity',
            format: 'grid',
            questions: [
              'secondProposedInsuredBirthDate',
              'secondProposedInsuredSsn',
              'secondProposedInsuredPlaceOfBirth',
            ],
          },
          {
            id: 'demographics',
            format: 'grid',
            questions: [
              'secondProposedInsuredGender',
              'secondProposedInsuredMarriedStatus',
              'secondProposedInsuredUsCitizen',
            ],
          },
          {
            id: 'non-us-citizen-info',
            format: 'default',
            questions: ['secondProposedNotUsCitizenDetails'],
          },
          {
            id: 'contact-info',
            format: 'grid',
            questions: ['secondProposedInsuredPhoneNumber', 'secondProposedInsuredEmailAddress'],
          },
          {
            id: 'employment',
            format: 'grid',
            questions: ['secondProposedInsuredOccupation', 'secondProposedInsuredEmployerName'],
          },
          {
            id: 'hidden-fields-for-signature-second',
            format: 'grid',
            hidden: true,
            questions: ['secondProposedInsuredFullName'],
          },
        ],
      },
    ],
  },

  /* 3 ▸ Owner ------------------------------------------------------- */
  owner: {
    id: 'owner',
    title: 'Owner',
    subsections: [
      {
        id: 'owner-info',
        title: 'Owner Information',
        questionGroups: [
          {
            id: 'owner-other-personal-info',
            format: 'grid',
            questions: [
              'ownerOtherFirstName',
              'ownerOtherMiddleInitial',
              'ownerOtherLastName',
              'ownerOtherFullName',
            ],
          },
          {
            id: 'relationship-gender',
            format: 'grid',
            questions: ['ownerRelationshipToInsured', 'ownerGender'],
          },
          {
            id: 'citizenship-email',
            format: 'grid',
            questions: ['ownerUsCitizen', 'ownerOtherEmail'],
          },
        ],
      },
      {
        id: 'entity-info',
        title: 'Corporation/Trust Information (if applicable)',
        questionGroups: [
          {
            id: 'entity-details',
            format: 'default',
            questions: ['ownerCorpTrustName', 'ownerCorpOfficerTrustee'],
          },
          {
            id: 'entity-type',
            format: 'default',
            questions: ['ownerEntityType', 'ownerOtherEntitySpecify'],
          },
          {
            id: 'entity-tax-address',
            format: 'default',
            questions: ['ownerSsnOrTaxId', 'ownerStreetAddress', 'ownerCityStateZip'],
          },
        ],
        conditional: {
          visible: {
            field: 'policyOwners',
            operator: 'in' as const,
            value: ['owner_trust', 'corporation'],
          },
        },
      },
    ],
    conditional: {
      visible: {
        field: 'policyOwners',
        operator: 'notIn' as const,
        value: ['owner_client', 'owner_joint', 'trust'],
      },
    },
  },

  /* 4 ▸ Joint Owner ------------------------------------------------------- */
  jointOwner: {
    id: 'joint-owner',
    title: 'Joint Owner (only complete if applicable)',
    subsections: [
      {
        id: 'joint-owner-info',
        title: 'Joint Owner Information',
        questionGroups: [
          {
            id: 'name-info',
            format: 'grid',
            questions: [
              'jointOwnerFirstName',
              'jointOwnerMiddleInitial',
              'jointOwnerLastName',
              'jointOwnerFullName',
            ],
          },
          {
            id: 'address-info',
            format: 'default',
            questions: ['jointOwnerStreetAddress', 'jointOwnerCityStateZip'],
          },
          {
            id: 'personal-details',
            format: 'grid',
            questions: ['jointOwnerBirthDate', 'jointOwnerSsn', 'jointOwnerPlaceOfBirth'],
          },
          {
            id: 'demographics-contact',
            format: 'grid',
            questions: ['jointOwnerGender', 'jointOwnerRelationshipToInsured'],
          },
          {
            id: 'contact-info',
            format: 'grid',
            questions: ['jointOwnerPhoneNumber', 'jointOwnerEmailAddress'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        field: 'policyOwners',
        operator: 'in' as const,
        value: ['owner_joint', 'owner_joint_other'],
      },
    },
  },

  /* 5 ▸ Beneficiary ------------------------------------------------------- */
  beneficiary: {
    id: 'beneficiary',
    title: 'Beneficiary',
    subsections: [
      {
        id: 'distribution-method',
        title: 'Distribution Method',
        questionGroups: [
          {
            id: 'distribution-selection',
            format: 'default',
            questions: ['beneficiaryDistributionMethod'],
          },
        ],
      },
      {
        id: 'primary-beneficiary',
        title: 'Primary Beneficiary',
        questionGroups: [
          {
            id: 'primary-table',
            format: 'default',
            questions: ['primaryBeneficiary'],
          },
          {
            id: 'primary-corporate',
            format: 'grid',
            questions: [
              'primaryBeneficiaryCorporateOfficer',
              'primaryBeneficiaryStateOfIncorporation',
            ],
          },
        ],
      },
      {
        id: 'secondary-beneficiary',
        title: 'Secondary Beneficiary',
        questionGroups: [
          {
            id: 'secondary-table',
            format: 'default',
            questions: ['secondaryBeneficiary'],
          },
          {
            id: 'secondary-corporate',
            format: 'grid',
            questions: [
              'secondaryBeneficiaryCorporateOfficer',
              'secondaryBeneficiaryStateOfIncorporation',
            ],
          },
        ],
      },
    ],
  },

  /* 6 ▸ Policy Selections ------------------------------------------------------- */
  policySelections: {
    id: 'policy-selections',
    title: 'Policy Selections',
    subsections: [
      {
        id: 'basic-selections',
        title: 'Basic Policy Information',
        questionGroups: [
          {
            id: 'face-amount',
            format: 'default',
            questions: ['policyFaceAmount'],
          },
          {
            id: 'product-funding',
            format: 'default',
            questions: ['policyProductFundingSelection'],
          },
          {
            id: 'single-return-cob',
            format: 'default',
            questions: ['policySingleReturnCob'],
            conditional: {
              visible: {
                field: 'policyProductFundingSelection',
                operator: 'equals',
                value: 'single-premium-with-return',
              },
            },
          },
          {
            id: 'recurring-premium-options',
            format: 'grid',
            questions: ['policyRecurringPayPeriod', 'policyRecurringBillingMode'],
            conditional: {
              visible: {
                field: 'policyProductFundingSelection',
                operator: 'equals',
                value: 'recurring-premium',
              },
            },
          },
        ],
      },
      {
        id: 'benefit-duration',
        title: 'Minimum Benefit Duration for Long-Term Care',
        questionGroups: [
          {
            id: 'aob-cob-selection',
            format: 'grid',
            questions: ['policyMinBenefitDurationAob', 'policyMinBenefitDurationCob'],
          },
        ],
        conditional: {
          visible: {
            field: 'policyProductFundingSelection',
            operator: 'notEquals',
            value: 'single-premium-with-return',
          },
        },
      },
      {
        id: 'cob-payment',
        title: 'COB Payment Period',
        questionGroups: [
          {
            id: 'cob-options',
            format: 'grid',
            questions: ['policyCobPaymentPeriod', 'policyCobRecurringBilling'],
          },
        ],
      },
      {
        id: 'inflation-protection',
        title: 'Inflation Protection',
        questionGroups: [
          {
            id: 'inflation-options',
            format: 'default',
            questions: ['policyInflationProtection', 'policyInflationDuration'],
          },
        ],
      },
      {
        id: 'additional-options',
        title: 'Additional Options',
        questionGroups: [
          {
            id: 'nonforfeiture',
            format: 'default',
            questions: ['policyNonforfeitureBenefit'],
          },
          {
            id: 'drop-in-rider',
            format: 'default',
            questions: [
              'policySinglePremiumDropIn',
              'policyDropInPaymentMethod',
              'policyTotalPremiumDropIn',
            ],
            conditional: {
              visible: {
                field: 'policyProductFundingSelection',
                operator: 'equals',
                value: 'recurring-premium',
              },
            },
          },
        ],
      },
      {
        id: 'premium-payment',
        title: 'Premium and Payment',
        questionGroups: [
          {
            id: 'total-premium',
            format: 'default',
            questions: ['policyTotalPremiumSelected'],
          },
          {
            id: 'payment-options',
            format: 'default',
            questions: ['policyPaymentOptions', 'policyMoneySubmitted'],
          },
        ],
      },
    ],
  },

  /* 7 ▸ Annual Income ------------------------------------------------------- */
  annualIncome: {
    id: 'annual-income',
    title: 'Annual Income of Proposed Insured',
    subsections: [
      {
        id: 'income-info',
        title: 'Income Information',
        questionGroups: [
          {
            id: 'income-details',
            format: 'grid',
            questions: ['incomeEarned', 'incomeUnearned', 'incomeNetWorth'],
          },
        ],
      },
    ],
  },

  /* 8 ▸ Protection Against Unintentional Lapse ------------------------------------------------------- */
  protectionAgainstLapse: {
    id: 'protection-against-lapse',
    title: 'Protection Against Unintentional Lapse',
    subsections: [
      {
        id: 'lapse-notice',
        title: 'Lapse Notice Designation',
        questionGroups: [
          {
            id: 'designation-choice',
            format: 'default',
            questions: ['lapseNoticeDesignation'],
          },
          {
            id: 'recipient-info',
            format: 'grid',
            questions: ['lapseNoticeRecipientName', 'lapseNoticeRecipientAddress'],
            conditional: {
              visible: {
                field: 'lapseNoticeDesignation',
                operator: 'equals',
                value: 'designate',
              },
            },
          },
        ],
      },
    ],
  },

  /* 10 ▸ Special Request / Additional Information ------------------------------------------------------- */
  specialRequest: {
    id: 'special-request',
    title: 'Special Request / Additional Information',
    subsections: [
      {
        id: 'replacement-details',
        title: '§1035 Replacement/Exchange Details',
        questionGroups: [
          {
            id: 'replacement-table',
            format: 'default',
            questions: ['specialReplacementDetails'],
          },
        ],
      },
      {
        id: 'additional-info',
        title: 'Additional Information',
        questionGroups: [
          {
            id: 'additional-notes',
            format: 'default',
            questions: ['specialAdditionalInfo'],
          },
        ],
      },
    ],
  },

  /* 11 ▸ Acknowledgement ------------------------------------------------------- */
  acknowledgement: {
    id: 'acknowledgement',
    title: 'Acknowledgement',
    subsections: [
      {
        id: 'premium-deposit',
        title: 'Premium Deposit Acknowledgement',
        questionGroups: [
          {
            id: 'deposit-status',
            format: 'default',
            questions: ['acknowledgementPremiumDeposit', 'acknowledgementDepositAmount'],
          },
        ],
      },
    ],
  },

  /* 12 ▸ Substitute W-9 Certification ------------------------------------------------------- */
  substituteW9: {
    id: 'substitute-w9',
    title: 'Substitute W-9 Certification',
    subsections: [
      {
        id: 'backup-withholding',
        title: 'Backup Withholding',
        questionGroups: [
          {
            id: 'withholding-status',
            format: 'default',
            questions: ['substituteW9BackupWithholding'],
          },
        ],
      },
    ],
  },

  /* 14 ▸ Asset Care Application Health Pre Questions ---------------------------------------- */
  healthPreQuestionSection: {
    id: 'health-pre-question-section',
    title: 'Medical Questionnaire Election: Form or Tele-Interview Process',
    subsections: [
      {
        id: 'form-or-tele-interview',
        title: 'Medical Questionnaire Election: Form or Tele-Interview Process',
        questionGroups: [
          {
            id: 'form-or-tele-interview-question',
            format: 'default',
            questions: ['healthPreQuestion'],
          },
        ],
      },
    ],
  },

  /* 14 ▸ Asset Care Application Checklist ------------------------------------------------------- */
  /* We need to keep the code for the future use */
  // assetCareApplicationChecklist: {
  //   id: 'asset-care-application-checklist',
  //   title: 'Asset Care Application Checklist',
  //   subsections: [
  //     {
  //       id: 'licensing-information',
  //       title: 'Licensing Information',
  //       questionGroups: [
  //         {
  //           id: 'agents-training-checklist',
  //           format: 'default',
  //           questions: ['agentsContractingAndTrainingConfirmation'],
  //         },
  //       ],
  //     },
  //     {
  //       id: 'underwriting-info',
  //       title: 'Underwriting Information',
  //       questionGroups: [
  //         {
  //           id: 'underwriting-info-checklist',
  //           format: 'default',
  //           questions: [
  //             'presentAssetCareIneligibleImpairments',
  //             'provideApplicationPreInterviewWorksheetI30110',
  //           ],
  //         },
  //       ],
  //     },
  //     {
  //       id: 'part-a-required-forms',
  //       title: 'Part A - Required Forms - Fax to OneAmerica 1-317-285-5235',
  //       questionGroups: [
  //         {
  //           id: 'part-a-required-forms-checklist',
  //           format: 'default',
  //           questions: ['partARequiredForms'],
  //         },
  //       ],
  //     },
  //     {
  //       id: 'part-b-supplemental-forms',
  //       title: 'Part B - Supplemental Forms - Fax to OneAmerica 1-317-285-5235',
  //       questionGroups: [
  //         {
  //           id: 'part-b-supplemental-forms-checklist',
  //           format: 'default',
  //           questions: ['partBSupplementalForms'],
  //         },
  //       ],
  //     },
  //     {
  //       id: 'part-c-application-forms',
  //       title: 'Part C - Application Forms - To be left with applicant(s)',
  //       questionGroups: [
  //         {
  //           id: 'part-c-application-forms-checklist',
  //           format: 'default',
  //           questions: ['partCApplicantForms'],
  //         },
  //       ],
  //     },
  //   ],
  // },

  /* 15 ▸ Medical Questionnaire Part 2 - Health Information (First Proposed Insured) ------------------------------------------------------- */
  medicalHealthInfoFirstProposedInsured: {
    id: 'medical-health-info-fpi',
    title: 'Medical Questionnaire Part 2 - Health Information for "{clientFullName}"',
    subsections: [
      {
        id: 'medical-health-info-fpi-questions',
        title: 'Medical Questionnaire Part 2 - Health Information for "{clientFullName}"',
        questionGroups: [
          {
            id: 'measurements-fpi',
            title: 'Physical Measurements',
            format: 'grid',
            questions: [
              'medicalFirstProposedInsuredHeightFT',
              'medicalFirstProposedInsuredHeightIN',
              'medicalFirstProposedInsuredWeight',
              'medicalFirstProposedInsuredWeightChange',
              'medicalFirstProposedInsuredWeightChangeAmount',
            ],
          },
          {
            id: 'heart-conditions-fpi',
            title: 'A. Medical History - During Past 10 Years',
            format: 'default',
            questions: ['medicalHeartConditionFirstInsured'],
          },
          {
            id: 'cancer-blood-fpi',
            format: 'default',
            questions: ['medicalCancerFirstInsured'],
          },
          {
            id: 'diabetes-kidney-fpi',
            format: 'default',
            questions: ['medicalDiabetesFirstInsured'],
          },
          {
            id: 'respiratory-fpi',
            format: 'default',
            questions: ['medicalLungDisorderFirstInsured'],
          },
          {
            id: 'digestive-fpi',
            format: 'default',
            questions: ['medicalIntestinalFirstInsured'],
          },
          {
            id: 'nervous-system-fpi',
            format: 'default',
            questions: ['medicalBrainNervousFirstInsured'],
          },
          {
            id: 'skeletal-system-fpi',
            format: 'default',
            questions: ['medicalArthritisFirstInsured'],
          },
          {
            id: 'sensory-organs-fpi',
            format: 'default',
            questions: ['medicalEyesEarsFirstInsured'],
          },
          {
            id: 'immune-system-fpi',
            format: 'default',
            questions: ['medicalAidsFirstInsured'],
          },
          {
            id: 'section-a-details-fpi',
            format: 'default',
            questions: ['medicalSectionADetailsFirstInsured'],
          },
          {
            id: 'hospitalizations-fpi',
            title: 'B. Medical History - During Past 5 Years',
            format: 'default',
            questions: ['medicalHospitalPatientFirstInsured'],
          },
          {
            id: 'nursing-home-fpi',
            format: 'default',
            questions: ['medicalNursingHomeFirstInsured'],
          },
          {
            id: 'daily-assistance-fpi',
            format: 'default',
            questions: ['medicalDailyAssistanceFirstInsured'],
          },
          {
            id: 'diagnostic-tests-fpi',
            format: 'default',
            questions: ['medicalDiagnosticTestsFirstInsured'],
          },
          {
            id: 'claims-benefits-fpi',
            format: 'default',
            questions: ['medicalClaimBenefitsFirstInsured'],
          },
          {
            id: 'falling-injury-fpi',
            format: 'default',
            questions: ['medicalFallingInjuryFirstInsured'],
          },
          {
            id: 'therapy-status-fpi',
            title: 'C. Current Medical Status',
            format: 'default',
            questions: ['medicalPhysicalTherapyFirstInsured'],
          },
          {
            id: 'assistive-devices-fpi',
            format: 'default',
            questions: ['medicalAssistiveDevicesFirstInsured'],
          },
          {
            id: 'medical-appliances-fpi',
            format: 'default',
            questions: ['medicalMedicalApplianceFirstInsured'],
          },
          {
            id: 'scheduled-care-fpi',
            format: 'default',
            questions: ['medicalScheduledDoctorFirstInsured'],
          },
          {
            id: 'pregnancy-fpi',
            format: 'default',
            questions: ['medicalPregnantFirstInsured'],
          },
          {
            id: 'other-treatment-fpi',
            format: 'default',
            questions: ['medicalOtherTreatmentFirstInsured'],
          },
          {
            id: 'physician-info-fpi',
            format: 'default',
            questions: [
              'medicalPrimaryCarePhysicianFirstInsuredName',
              'medicalPrimaryCarePhysicianFirstInsuredAddress',
            ],
          },
          {
            id: 'medications-fpi',
            format: 'default',
            questions: ['medicalMedicationsFirstInsured'],
          },
          {
            id: 'section-bc-details-fpi',
            format: 'default',
            questions: ['medicalSectionBCDetailsFirstInsured'],
          },
          {
            id: 'driving-violations-fpi',
            title: 'D. Driving and Legal History',
            format: 'default',
            questions: ['medicalDrivingViolationFirstInsured'],
          },
          {
            id: 'criminal-convictions-fpi',
            format: 'default',
            questions: ['medicalCriminalConvictionFirstInsured'],
          },
          {
            id: 'section-d-details-fpi',
            format: 'default',
            questions: ['medicalSectionDDetailsFirstInsured'],
          },
          {
            id: 'declined-status-fpi',
            title: 'E. Insurance History',
            format: 'default',
            questions: ['medicalInsuranceDeclinedFirstInsured'],
          },
          {
            id: 'section-e-details-fpi',
            format: 'default',
            questions: ['medicalSectionEDetailsFirstInsured'],
          },
          {
            id: 'drug-use-fpi',
            title: 'F. Substance Use History',
            format: 'default',
            questions: ['medicalDrugUseFirstInsured'],
          },
          {
            id: 'alcohol-treatment-fpi',
            format: 'default',
            questions: ['medicalAlcoholTreatmentFirstInsured'],
          },
          {
            id: 'self-help-groups-fpi',
            format: 'default',
            questions: ['medicalSelfHelpGroupFirstInsured'],
          },
          {
            id: 'activities-fpi',
            title: 'G. Hazardous Activities',
            format: 'default',
            questions: ['medicalHazardousActivitiesFirstInsured'],
          },
          {
            id: 'aviation-fpi',
            title: 'H. Aviation Activities',
            format: 'default',
            questions: ['medicalAviationFirstInsured'],
          },
          {
            id: 'foreign-travel-fpi',
            title: 'I. Foreign Travel Activities',
            format: 'default',
            questions: ['medicalForeignTravelFirstInsured'],
          },
          {
            id: 'armed-forces-fpi',
            title: 'J. Armed Forces Activities',
            format: 'default',
            questions: ['medicalArmedForcesFirstInsured'],
          },
          {
            id: 'tobacco-status-fpi',
            title: 'K. Tobacco and Nicotine Use',
            format: 'default',
            questions: ['medicalTobaccoUseFirstInsured'],
          },
          {
            id: 'tobacco-details-fpi',
            format: 'default',
            questions: ['medicalTobaccoDetailsFirstInsured'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        field: 'healthPreQuestion',
        operator: 'equals' as const,
        value: 'answer-medical-questions',
      },
    },
  },

  /* 15 ▸ Medical Questionnaire Part 2 - Health Information (Second Proposed Insured) ------------------------------------------------------- */
  medicalHealthInfoSecondProposedInsured: {
    id: 'medical-health-info-spi',
    title: 'Medical Questionnaire Part 2 - Health Information for "{clientPartnerFullName}"',
    subsections: [
      {
        id: 'medical-health-info-spi-questions',
        title: 'Medical Questionnaire Part 2 - Health Information for "{clientPartnerFullName}"',
        questionGroups: [
          {
            id: 'measurements-spi',
            title: 'Physical Measurements',
            format: 'grid',
            questions: [
              'medicalSecondProposedInsuredHeightFT',
              'medicalSecondProposedInsuredHeightIN',
              'medicalSecondProposedInsuredWeight',
              'medicalSecondProposedInsuredWeightChange',
              'medicalSecondProposedInsuredWeightChangeAmount',
            ],
          },
          {
            id: 'heart-conditions-spi',
            title: 'A. Medical History - During Past 10 Years',
            format: 'default',
            questions: ['medicalHeartConditionSecondInsured'],
          },
          {
            id: 'cancer-blood-spi',
            format: 'default',
            questions: ['medicalCancerSecondInsured'],
          },
          {
            id: 'diabetes-kidney-spi',
            format: 'default',
            questions: ['medicalDiabetesSecondInsured'],
          },
          {
            id: 'respiratory-spi',
            format: 'default',
            questions: ['medicalLungDisorderSecondInsured'],
          },
          {
            id: 'digestive-spi',
            format: 'default',
            questions: ['medicalIntestinalSecondInsured'],
          },
          {
            id: 'nervous-system-spi',
            format: 'default',
            questions: ['medicalBrainNervousSecondInsured'],
          },
          {
            id: 'skeletal-system-spi',
            format: 'default',
            questions: ['medicalArthritisSecondInsured'],
          },
          {
            id: 'sensory-organs-spi',
            format: 'default',
            questions: ['medicalEyesEarsSecondInsured'],
          },
          {
            id: 'immune-system-spi',
            format: 'default',
            questions: ['medicalAidsSecondInsured'],
          },
          {
            id: 'section-a-details-spi',
            format: 'default',
            questions: ['medicalSectionADetailsSecondInsured'],
          },
          {
            id: 'hospitalizations-spi',
            title: 'B. Medical History - During Past 5 Years',
            format: 'default',
            questions: ['medicalHospitalPatientSecondInsured'],
          },
          {
            id: 'nursing-home-spi',
            format: 'default',
            questions: ['medicalNursingHomeSecondInsured'],
          },
          {
            id: 'daily-assistance-spi',
            format: 'default',
            questions: ['medicalDailyAssistanceSecondInsured'],
          },
          {
            id: 'diagnostic-tests-spi',
            format: 'default',
            questions: ['medicalDiagnosticTestsSecondInsured'],
          },
          {
            id: 'claims-benefits-spi',
            format: 'default',
            questions: ['medicalClaimBenefitsSecondInsured'],
          },
          {
            id: 'falling-injury-spi',
            format: 'default',
            questions: ['medicalFallingInjurySecondInsured'],
          },
          {
            id: 'therapy-status-spi',
            title: 'C. Current Medical Status',
            format: 'default',
            questions: ['medicalPhysicalTherapySecondInsured'],
          },
          {
            id: 'assistive-devices-spi',
            format: 'default',
            questions: ['medicalAssistiveDevicesSecondInsured'],
          },
          {
            id: 'medical-appliances-spi',
            format: 'default',
            questions: ['medicalMedicalApplianceSecondInsured'],
          },
          {
            id: 'scheduled-care-spi',
            format: 'default',
            questions: ['medicalScheduledDoctorSecondInsured'],
          },
          {
            id: 'pregnancy-spi',
            format: 'default',
            questions: ['medicalPregnantSecondInsured'],
          },
          {
            id: 'other-treatment-spi',
            format: 'default',
            questions: ['medicalOtherTreatmentSecondInsured'],
          },
          {
            id: 'physician-info-spi',
            format: 'default',
            questions: [
              'medicalPrimaryCarePhysicianSecondInsuredName',
              'medicalPrimaryCarePhysicianSecondInsuredAddress',
            ],
          },
          {
            id: 'medications-spi',
            format: 'default',
            questions: ['medicalMedicationsSecondInsured'],
          },
          {
            id: 'section-bc-details-spi',
            format: 'default',
            questions: ['medicalSectionBCDetailsSecondInsured'],
          },
          {
            id: 'driving-violations-spi',
            title: 'D. Driving and Legal History',
            format: 'default',
            questions: ['medicalDrivingViolationSecondInsured'],
          },
          {
            id: 'criminal-convictions-spi',
            format: 'default',
            questions: ['medicalCriminalConvictionSecondInsured'],
          },
          {
            id: 'section-d-details-spi',
            format: 'default',
            questions: ['medicalSectionDDetailsSecondInsured'],
          },
          {
            id: 'declined-status-spi',
            title: 'E. Insurance History',
            format: 'default',
            questions: ['medicalInsuranceDeclinedSecondInsured'],
          },
          {
            id: 'section-e-details-spi',
            format: 'default',
            questions: ['medicalSectionEDetailsSecondInsured'],
          },
          {
            id: 'drug-use-spi',
            title: 'F. Substance Use History',
            format: 'default',
            questions: ['medicalDrugUseSecondInsured'],
          },
          {
            id: 'alcohol-treatment-spi',
            format: 'default',
            questions: ['medicalAlcoholTreatmentSecondInsured'],
          },
          {
            id: 'self-help-groups-spi',
            format: 'default',
            questions: ['medicalSelfHelpGroupSecondInsured'],
          },
          {
            id: 'activities-spi',
            title: 'G. Hazardous Activities',
            format: 'default',
            questions: ['medicalHazardousActivitiesSecondInsured'],
          },
          {
            id: 'aviation-spi',
            title: 'H. Aviation Activities',
            format: 'default',
            questions: ['medicalAviationSecondInsured'],
          },
          {
            id: 'foreign-travel-spi',
            title: 'I. Foreign Travel Activities',
            format: 'default',
            questions: ['medicalForeignTravelSecondInsured'],
          },
          {
            id: 'armed-forces-spi',
            title: 'J. Armed Forces Activities',
            format: 'default',
            questions: ['medicalArmedForcesSecondInsured'],
          },
          {
            id: 'tobacco-status-spi',
            title: 'K. Tobacco and Nicotine Use',
            format: 'default',
            questions: ['medicalTobaccoUseSecondInsured'],
          },
          {
            id: 'tobacco-details-spi',
            format: 'default',
            questions: ['medicalTobaccoDetailsSecondInsured'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'fundingPolicyType',
            operator: 'equals' as const,
            value: FundingPolicyType.jointAssetCare,
          },
          {
            field: 'healthPreQuestion',
            operator: 'equals' as const,
            value: 'answer-medical-questions',
          },
        ],
      },
    },
  },

  /* 16 ▸ US Patriot Act ------------------------------------------------------- */
  usPatriotAct: {
    id: 'us-patriot-act',
    title: 'US Patriot Act',
    subsections: [
      /* Product Information */
      {
        id: 'patriot-act-product-info',
        title: 'Product Information',
        questionGroups: [
          {
            id: 'patriot-act-product-info-selection',
            format: 'default',
            questions: ['patriotActProduct', 'patriotActPolicyNumber'],
          },
        ],
      },

      /* Source of Funds */
      {
        id: 'patriot-act-source-of-funds',
        title: '1. Source of Funds Used for Purchase',
        questionGroups: [
          {
            id: 'patriot-act-source-of-funds-selection',
            format: 'default',
            questions: ['patriotActSourceOfFunds', 'patriotActOtherSourceSpecify'],
          },
          {
            id: 'patriot-act-source-of-funds-premium-payer',
            format: 'grid',
            questions: ['patriotActPremiumPayerName', 'patriotActPremiumPayerRelationship'],
          },
        ],
      },

      /* Identification Verified - Owner */
      {
        id: 'patriot-act-identification-owner',
        title: 'Identification Verified (Owner/Trustee/Partner)',
        questionGroups: [
          {
            id: 'patriot-act-identification-owner-type',
            format: 'default',
            questions: ['patriotActOwnerType'],
          },
          {
            id: 'patriot-act-identification-owner-id-type',
            format: 'grid',
            questions: [
              'patriotActOwnerIdType',
              'patriotActOwnerDriversLicenseIssuer',
              'patriotActOwnerAlienIdIssuer',
              'patriotActOwnerPassportIssuer',
              'patriotActOwnerIdOtherDesc',
            ],
          },
          {
            id: 'patriot-act-identification-owner-id-details',
            format: 'grid',
            questions: ['patriotActOwnerName'],
          },
          {
            id: 'patriot-act-identification-owner-address',
            format: 'grid',
            questions: ['patriotActOwnerStreetAddress', 'patriotActOwnerCityStateZip'],
          },
          {
            id: 'patriot-act-identification-owner-id-numbers',
            format: 'grid',
            questions: [
              'patriotActOwnerIdNumber',
              'patriotActOwnerIdStateCountry',
              'patriotActOwnerIdExpirationDate',
            ],
          },
        ],
      },

      /* Identification Verified - Joint Owner */
      {
        id: 'patriot-act-identification-joint-owner',
        title: 'Identification Verified (Joint Owner/Trustee/Partner)',
        questionGroups: [
          {
            id: 'patriot-act-identification-joint-owner-id-type',
            format: 'default',
            questions: ['patriotActJointOwnerType'],
          },
          {
            id: 'patriot-act-identification-joint-owner-id-type',
            format: 'grid',
            questions: [
              'patriotActJointOwnerIdType',
              'patriotActJointOwnerDriversLicenseIssuer',
              'patriotActJointOwnerAlienIdIssuer',
              'patriotActJointOwnerPassportIssuer',
              'patriotActJointOwnerIdOtherDesc',
            ],
          },
          {
            id: 'patriot-act-identification-joint-owner-id-details',
            format: 'grid',
            questions: ['patriotActJointOwnerName'],
          },
          {
            id: 'patriot-act-identification-joint-owner-address',
            format: 'grid',
            questions: ['patriotActJointOwnerStreetAddress', 'patriotActJointOwnerCityStateZip'],
          },
          {
            id: 'patriot-act-identification-joint-owner-id-numbers',
            format: 'grid',
            questions: [
              'patriotActJointOwnerIdNumber',
              'patriotActJointOwnerIdStateCountry',
              'patriotActJointOwnerIdExpirationDate',
            ],
          },
        ],
        conditional: {
          visible: {
            operator: 'or',
            conditions: [
              { field: 'policyOwners', operator: 'notEquals', value: 'owner_client' },
              { field: 'policyPayor', operator: 'notEquals', value: 'payor_client' },
            ],
          },
        },
      },

      /* Entity Verification */
      {
        id: 'patriot-act-entity-verification',
        title: 'Entity Verification',
        questionGroups: [
          {
            id: 'patriot-act-entity-verification-documents',
            format: 'default',
            questions: ['patriotActEntityDocuments', 'patriotActEntityOtherDesc'],
          },
        ],
        conditional: {
          visible: {
            operator: 'or',
            conditions: [
              {
                operator: 'and',
                conditions: [
                  { field: 'policyOwners', operator: 'notEquals', value: 'owner_client' },
                  { field: 'policyPayor', operator: 'notEquals', value: 'payor_client' },
                ],
              },
              {
                operator: 'and',
                conditions: [
                  { field: 'policyOwners', operator: 'notEquals', value: 'owner_joint' },
                  { field: 'policyPayor', operator: 'notEquals', value: 'payor_client' },
                ],
              },
            ],
          },
        },
      },

      /* Certification */
      {
        id: 'patriot-act-certification',
        title: 'Certification',
        questionGroups: [
          {
            id: 'patriot-act-certification-meeting',
            format: 'default',
            questions: ['patriotActCertification'],
          },
          {
            id: 'patriot-act-certification-review',
            format: 'default',
            questions: ['patriotActReasonNoReview'],
          },
        ],
      },

      /* Producer Information */
      {
        id: 'patriot-act-producer-info',
        title: 'Producer Information',
        questionGroups: [
          {
            id: 'patriot-act-producer-info-details',
            format: 'grid',
            questions: ['patriotActProducerName', 'patriotActProducerNumber'],
          },
        ],
      },
    ],
  },

  /* 17 ▸ Authorization for Disclosure of Personal Information ------------------------------------------------------- */
  authDisclosurePersonalInfo: {
    id: 'auth-disclosure-personal-info',
    title: 'Authorization for Disclosure of Personal Information',
    subsections: [
      /* Header Information */
      {
        id: 'auth-disclosure-header-info',
        title: 'Authorization for Disclosure of Personal Information (Medical Information)',
        questionGroups: [
          {
            id: 'auth-disclosure-header-info-fields-attn',
            title:
              'This form should be completed by the applicant/policy owner or the insured/annuitant.',
            description:
              'Use this form to let the insurance company share your information for policy administration, claim processing, or underwriting.',
            format: 'default',
            questions: ['authDisclosureAttention'],
          },
          {
            id: 'auth-disclosure-header-info-fields',
            format: 'grid',
            questions: [
              'authDisclosureApplicantPolicyOwners',
              'authDisclosureApplicantPolicyI',
              'authDisclosureProposedInsuredAnnuitant',
              'authDisclosurePolicyNumber',
            ],
          },
          {
            id: 'auth-disclosure-company-authorization-fields',
            format: 'default',
            questions: ['authDisclosureCompanyAuthorized'],
          },
          {
            id: 'auth-disclosure-info-description-fields',
            format: 'default',
            questions: ['authDisclosureInformationType'],
          },
          {
            id: 'auth-disclosure-recipient-info-fields',
            title: 'Person or Business Authorized to Receive Information',
            format: 'grid',
            questions: [
              'authDisclosureRecipientName',
              'authDisclosureRecipientAddress',
              'authDisclosureRecipientCity',
              'authDisclosureRecipientState',
              'authDisclosureRecipientZip',
            ],
          },
          {
            id: 'auth-disclosure-expiration-fields',
            title: 'Expiration of Authorization',
            description:
              'The authorization will expire 24 months from the date of your signature. (Only valid for one year in Connecticut, Georgia, Illinois, Massachusetts, Minnesota, North Carolina, New Jersey, Ohio, and Oregon.)',
            format: 'default',
            questions: ['authDisclosureDaytimePhone', 'authDisclosureStateOfSigning'],
          },
          {
            id: 'auth-disclosure-representative-authority-fields',
            format: 'default',
            questions: ['authDisclosureRepresentativeAuthority'],
          },
        ],
      },
    ],
  },

  /* 18 ▸ Premium (Annual/Single) ------------------------------------------------------- */
  ltcPremiumAnnualSingle: {
    id: 'ltc-premium-annual-single',
    title: 'Premium (Annual/Single)',
    subsections: [
      {
        id: 'ltc-premium-fields',
        title: 'Premium Details',
        questionGroups: [
          {
            id: 'ltc-premium-fields-group',
            format: 'grid',
            questions: [
              'ltcPremiumForPolicy',
              'ltcPremiumForAobRider',
              'ltcPremiumForInflationProtectionRider',
            ],
          },
        ],
      },
    ],
  },

  /* 19 ▸ Premium (Annual/Single) ------------------------------------------------------- */
  ltcPremiumAnnualSingleRider: {
    id: 'ltc-premium-annual-single-rider',
    title: 'Premium (Annual/Single) (Rider)',
    subsections: [
      {
        id: 'ltc-premium-fields-rider',
        title: 'Premium Details',
        questionGroups: [
          {
            id: 'ltc-premium-fields-group',
            format: 'grid',
            questions: [
              'ltcPremiumForThisRider',
              'ltcPremiumForInflationProtectionRider2',
              'ltcPremiumForNonforfeitureBenefitRider',
            ],
          },
        ],
      },
    ],
  },

  /* 20 ▸ Request of Funds Via ------------------------------------------------------- */
  rofVia1035Exchange: {
    id: 'rof-via-1035-exchange',
    title:
      'Request of Funds Via: Full 1035 Exchange, IRA Transfer, Direct Rollover, CD Assignment, Mutual Fund Transfer',
    subsections: [
      {
        id: 'rof-via-1035-exchange-questions',
        title:
          'Request of Funds Via: Full 1035 Exchange, IRA Transfer, Direct Rollover, CD Assignment, Mutual Fund Transfer',
        questionGroups: [
          {
            id: 'pirc-fields-group-1',
            format: 'grid',
            questions: [
              'rofCurrentCustodianCompany',
              'rofCurrentCustodianTelephone',
              'rofCurrentPolicyAccountNumbers',
            ],
          },
          {
            id: 'pirc-fields-group-2',
            format: 'grid',
            questions: ['rofCustodianAddress', 'rofCustodianCity'],
          },
          {
            id: 'pirc-fields-group-3',
            format: 'grid',
            questions: ['rofCustodianState', 'rofCustodianZip'],
          },
          {
            id: 'pirc-fields-group-4',
            format: 'grid',
            questions: ['rofCustomerPolicyownerTaxpayer', 'rofTaxpayerIdentificationNumber'],
          },
          {
            id: 'pirc-fields-group-5',
            format: 'grid',
            questions: ['rofEstimatedPolicyAccountValue', 'rofNameOfInsuredAnnuitant'],
          },
          {
            id: 'pirc-fields-group-6',
            format: 'default',
            questions: ['rofFundsBeingTransferredInto'],
          },
          {
            id: 'pirc-fields-group-7',
            format: 'default',
            questions: ['rofMustCheckOne'],
          },
          {
            id: 'pirc-fields-group-8',
            format: 'default',
            questions: ['rofMustCheckOnePolicy'],
          },
        ],
      },
      // Section 1035 Exchanges
      {
        id: 'section-1035-exchanges',
        title: 'Section 1035 Exchanges',
        questionGroups: [
          {
            id: 'section-1035-fields-group-1',
            format: 'default',
            questions: ['rofFundsTransferredFrom'],
          },
        ],
        conditional: {
          visible: {
            field: 'rofMustCheckOne',
            value: '1035-exchanges',
            operator: 'equals',
          },
        },
      },
      // Qualified Funds (Choose one option from A, B, and C)
      {
        id: 'qualified-funds-abc',
        title: 'Qualified Funds (Choose one option from A, B, and C)',
        questionGroups: [
          {
            id: 'qualified-funds-abc-group-1',
            format: 'default',
            questions: ['rofQualifiedCurrentStatus'],
          },
          {
            id: 'qualified-funds-abc-group-1-related',
            format: 'grid',
            questions: ['rofQualifiedOtherSpecify'],
          },
          {
            id: 'qualified-funds-abc-group-2',
            format: 'default',
            questions: ['rofQualifiedTransferType'],
          },
          {
            id: 'qualified-funds-abc-group-3',
            format: 'default',
            questions: ['rofQualifiedAmountOfFunds'],
          },
          {
            id: 'qualified-funds-abc-group-3-related',
            format: 'grid',
            questions: ['rofQualifiedDollarAmount', 'rofQualifiedPercentage'],
          },
        ],
        conditional: {
          visible: {
            field: 'rofMustCheckOne',
            value: 'qualified-funds',
            operator: 'equals',
          },
        },
      },
      // Other Funds (Choose one option from A and B)
      {
        id: 'other-funds-ab',
        title: 'Other Funds (Choose one option from A and B)',
        questionGroups: [
          {
            id: 'other-funds-ab-group-1',
            format: 'grid',
            questions: ['rofOtherFundsType', 'rofOtherFundsCdMaturityDate'],
          },
          {
            id: 'other-funds-ab-group-2',
            format: 'default',
            questions: ['rofOtherFundsAmount'],
          },
          {
            id: 'other-funds-ab-group-2-related',
            format: 'grid',
            questions: ['rofOtherFundsDollarAmount', 'rofOtherFundsPercentage'],
          },
        ],
        conditional: {
          visible: {
            field: 'rofMustCheckOne',
            value: 'other-funds',
            operator: 'equals',
          },
        },
      },
    ],
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          { field: 'paymentMethodAlternative', operator: 'equals', value: 'yes' },
          { field: 'paymentMethod', operator: 'notEquals', value: 'partial_1035' },
        ],
      },
    },
  },

  /* 22 ▸ Electronic Premium Payment Authorization ------------------------------------------------------- */
  electronicPremiumPaymentAuthorization: {
    id: 'electronic-premium-payment-authorization',
    title: 'Electronic Premium Payment Authorization',
    subsections: [
      {
        id: 'epa-premium-authorization',
        title: 'Electronic Premium Payment Authorization',
        questionGroups: [
          {
            id: 'epa-transaction-group',
            title: 'Transaction Type',
            format: 'grid',
            questions: ['epaTransactionType', 'epaPolicyNumber'],
          },
          {
            id: 'epa-one-time-group',
            title: 'One-Time Premium Payment',
            format: 'grid',
            questions: ['epaOneTimePremiumAuth', 'epaOneTimePremiumAmount'],
          },
          {
            id: 'epa-recurring-existing-group',
            title: 'Recurring Premium Payments',
            format: 'grid',
            questions: ['recurringPremiumPayments', 'epaRecurringExistingPolicyNumber'],
          },
          {
            id: 'epa-bank-routing-account',
            title: 'Bank Account Information',
            format: 'grid',
            questions: ['epaBankRoutingNumber', 'epaBankAccountNumber'],
          },
          {
            id: 'epa-bank-types',
            format: 'grid',
            questions: ['epaBankAccountType', 'epaBankAccountCategory'],
          },
          {
            id: 'epa-bank-contact',
            format: 'grid',
            questions: ['epaAccountholderName', 'epaAccountholderPhone', 'epaAccountholderEmail'],
          },
          {
            id: 'epa-bank-address',
            format: 'grid',
            questions: [
              'epaAccountholderStreetAddress',
              'epaAccountholderCity',
              'epaAccountholderState',
              'epaAccountholderZipCode',
            ],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          {
            field: 'policyPaymentOptions',
            operator: 'equals',
            value: 'cash',
          },
          {
            field: 'policyMoneySubmitted',
            operator: 'equals',
            value: 'yes',
          },
        ],
      },
    },
  },

  /* 24 ▸ LTC Insurance Personal Worksheet ------------------------------------------------------- */
  temporaryInsuranceAgreement: {
    id: 'temporary-insurance-agreement',
    title: 'Temporary Insurance Agreement',
    subsections: [
      /* Health Question */
      {
        id: 'tia-health-question',
        title: 'Health Question',
        questionGroups: [
          {
            id: 'tia-health-question-group',
            format: 'default',
            questions: ['healthQuestion90Days', 'healthQuestionProposedInsuredName'],
          },
        ],
      },

      /* Premium Deposit */
      {
        id: 'tia-premium-deposit',
        title: 'Premium Deposit',
        questionGroups: [
          {
            id: 'tia-premium-deposit-group',
            format: 'grid',
            questions: ['premiumDepositReceivedFrom', 'premiumDepositAmount'],
          },
        ],
      },

      /* Termination */
      {
        id: 'tia-termination',
        title: 'Termination of Temporary Insurance',
        questionGroups: [
          {
            id: 'tia-termination-group',
            title: ' ',
            description: `Life Insurance under this Agreement will terminate with respect to any Proposed Insured on the earliest of:
1. The date that insurance coverage begins under the policy applied for or under a policy issued other than as applied for.
2. Ten (10) days after a policy other than as applied for is offered to any Proposed Insured or Owner.
3. Five (5) days after the Company mails a letter of declination to any Proposed Insured or Owner.
4. Sixty (60) days after the date of the application.\n\n

**Fraud Warning**: Any person who knowingly and with intent to injure, defraud or deceive any insurer files a statement of claim or an application containing any false, incomplete or misleading information is guilty of a felony of the third degree. I/We have read this Agreement and understand and agree to its terms. I/We understand this Agreement provides no insurance unless all of its conditions are met and all required medical exams are completed. I/We declare that the answer to the Health Question is true and complete.`,
            format: 'grid',
            questions: ['representativeOrAgent', 'tempInsuranceOwner'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          { field: 'policyMoneySubmitted', operator: 'equals', value: 'yes' },
          { field: 'acknowledgementPremiumDeposit', operator: 'equals', value: 'with-deposit' },
        ],
      },
    },
  },

  /* 25 ▸ Alcohol Use Supplement ------------------------------------------------------- */
  alcoholUseSupplement: {
    id: 'alcohol-use-supplement',
    title: 'Alcohol Use Supplement',
    subsections: [
      {
        id: 'alcohol-use-questions',
        title: 'Alcohol Use Supplement',
        questionGroups: [
          {
            id: 'alcohol-use-current-beer',
            title: '1. Current Alcohol Use',
            description: 'Do you presently use any of the following alcoholic beverages?',
            format: 'default',
            questions: ['alcoholUseBeer'],
          },
          {
            id: 'alcohol-use-current-beer-interval',
            format: 'grid',
            questions: ['alcoholUseBeerDaily', 'alcoholUseBeerWeekly', 'alcoholUseBeerMonthly'],
          },
          {
            id: 'alcohol-use-current-wine',
            format: 'default',
            questions: ['alcoholUseWine'],
          },
          {
            id: 'alcohol-use-current-wine-interval',
            format: 'grid',
            questions: ['alcoholUseWineDaily', 'alcoholUseWineWeekly', 'alcoholUseWineMonthly'],
          },
          {
            id: 'alcohol-use-current-liquor',
            format: 'default',
            questions: ['alcoholUseLiquor'],
          },
          {
            id: 'alcohol-use-current-liquor-interval',
            format: 'grid',
            questions: [
              'alcoholUseLiquorDaily',
              'alcoholUseLiquorWeekly',
              'alcoholUseLiquorMonthly',
            ],
          },
          {
            id: 'alcohol-use-last-drink-group',
            format: 'default',
            questions: ['alcoholLastDrink'],
          },
          {
            id: 'alcohol-use-treatment-group',
            title: 'Treatment To Reduce Alcohol Use',
            format: 'default',
            questions: ['alcoholTreatment'],
          },
          {
            id: 'alcohol-use-past-history-table',
            title: 'Past Alcohol Use (More Than Present)',
            format: 'default',
            questions: ['alcoholDrinkMore', 'alcoholPastDrinkingHistory'],
          },
          {
            id: 'alcohol-use-change-reason',
            format: 'default',
            questions: ['alcoholChangeReason'],
          },
          {
            id: 'alcohol-use-treatment-history-group',
            title: '5. Treatment History',
            format: 'default',
            questions: ['alcoholAdvisedReduce', 'alcoholTreatmentDetails'],
          },
          {
            id: 'alcohol-use-dui-group',
            title: '6. DUI or Accidents Involving Alcohol',
            format: 'default',
            questions: ['alcoholDuiAccidents', 'alcoholDuiDetails'],
          },
          {
            id: 'alcohol-use-support-group',
            title: '7. Support Groups',
            format: 'default',
            questions: ['alcoholSupportGroup'],
          },
          {
            id: 'alcohol-use-support-group-related',
            format: 'grid',
            questions: ['alcoholSupportGroupDateAttended', 'alcoholSupportGroupFrequency'],
          },
          {
            id: 'alcohol-use-relapse-group',
            title: '8. Relapse',
            format: 'default',
            questions: ['alcoholRelapse', 'alcoholRelapseDetails'],
          },
          {
            id: 'alcohol-use-additional-info-group',
            title: '9. Additional Information',
            format: 'default',
            questions: ['alcoholAdditionalInformation'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          { field: 'medicalAlcoholTreatmentFirstInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalAlcoholTreatmentSecondInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalSelfHelpGroupFirstInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalSelfHelpGroupSecondInsured', operator: 'equals', value: 'yes' },
        ],
      },
    },
  },

  /* 27 ▸ Aviation Supplement ------------------------------------------------------- */
  aviationSupplement: {
    id: 'aviation-supplement',
    title: 'Aviation Supplement',
    subsections: [
      /* Q1: Pilot Status and Flying Hours */
      {
        id: 'aviation-pilot-status-section',
        title: '1. Pilot Status and Flying Activity',
        questionGroups: [
          {
            id: 'aviation-pilot-status-group',
            format: 'default',
            questions: ['aviationPilotStatus'],
          },
          {
            id: 'aviation-civilian-hours-group',
            title: 'Civilian Aircraft Activity',
            format: 'default',
            questions: ['aviationCivilianHoursTable'],
          },
          {
            id: 'aviation-activity-types-group',
            title: 'Type of Civilian Aircraft Activity and Hours',
            format: 'default',
            questions: ['aviationActivityTypes'],
          },
          {
            id: 'aviation-activity-types-hours-group',
            format: 'grid',
            questions: [
              'aviationActivityHours13to24MonthsAgo',
              'aviationActivityHoursLast12Months',
              'aviationActivityHoursEstimatedNext12Months',
            ],
          },
          {
            id: 'aviation-civilian-total-hours',
            format: 'grid',
            title: 'Civilian Aircraft Activity Total Hours',
            questions: [
              'hours13to24MonthsAgoTotal',
              'hoursLast12MonthsTotal',
              'hoursEstimatedNext12MonthsTotal',
            ],
          },
          {
            id: 'aviation-military-hours-group',
            format: 'default',
            title: 'Military Aircraft Activity',
            questions: ['aviationMilitaryHoursTable'],
          },
          {
            id: 'aviation-military-aircraft-types-group',
            format: 'default',
            title: 'Type of Military Aircraft Activity and Hours',
            questions: ['aviationMilitaryAircraftTypes'],
          },
          {
            id: 'aviation-military-aircraft-types-hours-group',
            format: 'grid',
            questions: [
              'aviationMilitaryHours13to24MonthsAgo',
              'aviationMilitaryHoursLast12Months',
              'aviationMilitaryHoursEstimatedNext12Months',
            ],
          },
          {
            id: 'aviation-military-total-hours',
            format: 'grid',
            title: 'Military Aircraft Activity Total Hours',
            questions: [
              'hours13to24MonthsAgoTotalAviationMilitary',
              'hoursLast12MonthsTotalAviationMilitary',
              'hoursEstimatedNext12MonthsTotalAviationMilitary',
            ],
          },
        ],
      },

      /* Q2: Total Hours and Last Flight */
      {
        id: 'aviation-flight-experience',
        title: '2. Flight Experience',
        questionGroups: [
          {
            id: 'aviation-flight-experience-group',
            format: 'grid',
            questions: ['aviationTotalHoursFlown', 'aviationDateLastFlight'],
          },
        ],
      },

      /* Q3: Instrument Rating */
      {
        id: 'aviation-instrument-rating-section',
        title: '3. Instrument Flight Rating',
        questionGroups: [
          {
            id: 'aviation-instrument-rating-group',
            format: 'default',
            questions: ['aviationInstrumentRating'],
          },
        ],
      },

      /* Q4: License Status and Accidents */
      {
        id: 'aviation-license-accidents',
        title: '4. License Status and Aviation Accidents',
        questionGroups: [
          {
            id: 'aviation-license-accidents-group',
            format: 'default',
            questions: ['aviationLicenseRevoked', 'aviationLicenseRevokedExplain'],
          },
        ],
      },

      /* Q5: Past Aircraft Types */
      {
        id: 'aviation-past-aircraft',
        title: '5. Aircraft Flown in Past Three Years',
        questionGroups: [
          {
            id: 'aviation-past-aircraft-group',
            format: 'default',
            questions: ['aviationPastThreeYears'],
          },
        ],
      },

      /* Q6: Crop Dusting Details */
      {
        id: 'aviation-crop-dusting',
        title: '6. Crop Dusting Operations',
        questionGroups: [
          {
            id: 'aviation-crop-dusting-type-group',
            format: 'default',
            questions: ['aviationCropDustingType'],
          },
          {
            id: 'aviation-crop-dusting-details-group',
            format: 'grid',
            questions: [
              'aviationCropDustingBase',
              'aviationNighttimeSpraying',
              'aviationCropSprayingHours',
            ],
          },
        ],
      },

      /* Q7: Crew Member Status */
      {
        id: 'aviation-crew-member',
        title: '7. Crew Member Status',
        questionGroups: [
          {
            id: 'aviation-crew-member-group',
            format: 'default',
            questions: ['aviationCrewMember', 'aviationCrewMemberDetails'],
          },
        ],
      },

      /* Q8: Coverage Preference */
      {
        id: 'aviation-coverage-preference',
        title: '8. Coverage Preference',
        questionGroups: [
          {
            id: 'aviation-coverage-preference-group',
            format: 'default',
            questions: ['aviationCoveragePreference'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          { field: 'medicalAviationFirstInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalAviationSecondInsured', operator: 'equals', value: 'yes' },
        ],
      },
    },
  },

  /* 28 ▸ Drug Use Supplement ------------------------------------------------------- */
  drugUseSupplement: {
    id: 'drug-use-supplement',
    title: 'Drug Use Supplement',
    subsections: [
      {
        id: 'drug-use-questions',
        title: 'Drug Use Supplement',
        questionGroups: [
          {
            id: 'drug-use-substances-group',
            title: '1. Drug Use History',
            format: 'default',
            questions: [
              'drugUseOpiates',
              'drugUseBarbiturates',
              'drugUseNonBarbiturates',
              'drugUseAmphetamines',
              'drugUseAnticholinergics',
              'drugUseHallucinogens',
              'drugUseCannabis',
              'drugUseOtherHabitForming',
            ],
          },
          {
            id: 'drug-use-details-group',
            format: 'default',
            questions: ['drugUseDetailsTable'],
          },
          {
            id: 'drug-use-arrests-group',
            title: '2. Drug-Related Arrests',
            format: 'default',
            questions: ['drugUseArrested', 'drugUseArrestedDetails'],
          },
          {
            id: 'drug-use-assistance-group',
            title: '3. Treatment and Assistance',
            format: 'default',
            questions: ['drugUseSeekAssistance', 'drugUseAssistanceDetails'],
          },
          {
            id: 'drug-use-relapse-group',
            title: '4. Relapse History',
            format: 'default',
            questions: ['drugUseRelapse', 'drugUseRelapseDetails'],
          },
          {
            id: 'drug-use-support-group',
            title: '5. Support Groups',
            format: 'default',
            questions: ['drugUseSupportGroups', 'drugUseSupportGroupsLastAttended'],
          },
          {
            id: 'drug-use-additional-group',
            title: '6. Additional Remarks',
            format: 'default',
            questions: ['drugUseAdditionalRemarks'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          { field: 'medicalDrugUseFirstInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalDrugUseSecondInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalSelfHelpGroupFirstInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalSelfHelpGroupSecondInsured', operator: 'equals', value: 'yes' },
          {
            field: 'medicalNonPrescribedDrugsTreatmentFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalNonPrescribedDrugsTreatmentSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalPrescribedMedicationTreatmentFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalPrescribedMedicationTreatmentSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalNarcoticsUseFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalNarcoticsUseSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalSelfHelpGroupFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalSelfHelpGroupSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalNarcoticsUseFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalNarcoticsUseSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalBarbituratesUseFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalBarbituratesUseSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalAmphetaminesUseFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalAmphetaminesUseSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalHallucinogensUseFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalHallucinogensUseSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalHeroinUseFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalHeroinUseSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalCocaineUseFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalCocaineUseSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalMarijuanaUseFirstInsured',
            operator: 'equals',
            value: 'yes',
          },
          {
            field: 'medicalMarijuanaUseSecondInsured',
            operator: 'equals',
            value: 'yes',
          },
        ],
      },
    },
  },

  /* 29 ▸ Armed Forces Supplement ------------------------------------------------------- */
  armedForcesSupplement: {
    id: 'armed-forces-supplement',
    title: 'Armed Forces Supplement',
    subsections: [
      /* Section A - Military Status */
      {
        id: 'military-status-section-a',
        title: 'A. Military Status',
        questionGroups: [
          /* Q1: Active Duty */
          {
            id: 'military-active-duty-group',
            format: 'default',
            questions: ['militaryActiveDuty'],
          },
          {
            id: 'military-active-details-group',
            format: 'grid',
            questions: [
              'militaryActiveBranch',
              'militaryActiveBranchDetails',
              'militaryActivePayGrade',
              'militaryActiveJobTitle',
            ],
          },
          {
            id: 'military-active-duties-group',
            format: 'default',
            questions: ['militaryActiveJobDuties'],
          },
          {
            id: 'military-active-service-length-group',
            format: 'grid',
            questions: ['militaryActiveLengthFrom', 'militaryActiveLengthTo'],
          },
          /* Q2: Reserve/Guard */
          {
            id: 'military-reserve-guard-group',
            format: 'default',
            questions: ['militaryReserveGuard'],
          },
          {
            id: 'military-reserve-details-group',
            format: 'grid',
            questions: [
              'militaryReserveStatus',
              'militaryReserveBranch',
              'militaryReserveBranchDetails',
              'militaryReservePayGrade',
              'militaryReserveJobTitle',
              'militaryReserveTraining',
            ],
          },
          /* Q3-7: Additional Questions */
          {
            id: 'military-additional-questions-group',
            format: 'default',
            questions: [
              'militaryAlertedActive',
              'militaryHazardousPay',
              'militarySpecialTraining',
              'militaryDutyOutside',
              'militaryAviationIntent',
            ],
          },
          {
            id: 'military-remarks-group',
            format: 'default',
            questions: ['militaryRemarks'],
          },
        ],
      },

      /* Section B - Military Aviation */
      {
        id: 'military-aviation-section-b',
        title: 'B. Military Aviation',
        questionGroups: [
          {
            id: 'military-aviation-service-group',
            format: 'default',
            questions: ['militaryAviationService', 'militaryAviationOtherSpecify'],
          },
          {
            id: 'military-aviation-activity-group',
            format: 'default',
            questions: ['militaryAviationActivity'],
          },
          {
            id: 'military-aviation-assignment-group',
            format: 'grid',
            questions: ['militaryAviationDutyAssignment', 'militaryAviationAircraft'],
          },
          {
            id: 'military-aviation-status-group',
            format: 'default',
            questions: [
              'militaryAviationActivityChange',
              'militaryAviationAerobatic',
              'militaryAviationProficiency',
            ],
          },
          {
            id: 'military-aviation-remarks-b-group',
            format: 'default',
            questions: ['militaryAviationRemarks'],
          },
        ],
      },

      /* Section C - Aviation Supplement */
      {
        id: 'military-aviation-section-c',
        title: 'C. Aviation Supplement',
        questionGroups: [
          {
            id: 'military-aviation-flight-hours-group',
            format: 'default',
            questions: ['militaryAviationCivilianTable', 'militaryAviationMilitaryTable'],
          },
          {
            id: 'military-aviation-other-flying-group',
            format: 'default',
            questions: ['militaryAviationOtherFlying'],
          },
          {
            id: 'military-aviation-service-details-group',
            format: 'grid',
            questions: ['militaryAviationBranchService', 'militaryAviationRank'],
          },
          {
            id: 'military-aviation-other-duties-group',
            format: 'default',
            questions: ['militaryAviationOtherDuties'],
          },
          {
            id: 'military-aviation-ownership-group',
            format: 'default',
            questions: [
              'militaryAviationOwnPlane',
              'militaryAviationFlyPlans',
              'militaryAviationFlyPlansDetails',
            ],
          },
          {
            id: 'military-aviation-license-group',
            format: 'default',
            questions: ['militaryAviationPilotLicense'],
          },
          {
            id: 'military-aviation-license-details-group',
            format: 'grid',
            questions: [
              'militaryAviationLicenseType',
              'militaryAviationLicenseIssueDate',
              'militaryAviationLicenseRenewalDate',
              'militaryAviationLastFlightDate',
            ],
          },
          {
            id: 'military-aviation-coverage-group',
            format: 'default',
            questions: [
              'militaryAviationCoveragePreference',
              'militaryAviationCoveragePreferenceA',
              'militaryAviationCoveragePreferenceB',
            ],
          },
          {
            id: 'military-aviation-additional-remarks-group',
            format: 'default',
            questions: ['militaryAviationAdditionalRemarks'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          { field: 'medicalArmedForcesFirstInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalArmedForcesSecondInsured', operator: 'equals', value: 'yes' },
        ],
      },
    },
  },

  /* 30 ▸ Avocation Supplement ------------------------------------------------------- */
  avocationSupplement: {
    id: 'avocation-supplement',
    title: 'Avocation Supplement',
    subsections: [
      /* Section 1 - Racing */
      {
        id: 'avocation-supplement-questions',
        title: 'Avocation Supplement',
        questionGroups: [
          {
            id: 'racing-engagement-group',
            title: '1. Racing',
            format: 'default',
            questions: ['racingEngagement'],
          },
          {
            id: 'racing-details-table-group',
            format: 'default',
            questions: ['racingDetailsTable'],
          },
          {
            id: 'racing-vehicle-group',
            format: 'grid',
            questions: [
              'racingOwnVehicle',
              'racingOwnVehicleConfirm',
              'racingVehicleClass',
              'racingVehicleEngine',
              'racingVehicleHorsepower',
            ],
          },
          {
            id: 'racing-experience-group',
            format: 'default',
            questions: [
              'racingDriverSchool',
              'racingCompetitionLicense',
              'racingParticipationLength',
              'racingSanctionedEvents',
              'racingOtherPurpose',
              'racingStuntDriving',
              'racingOutsideUS',
            ],
          },
          {
            id: 'racing-last-race-group',
            format: 'grid',
            questions: ['racingLastRaceDate', 'racingLastRaceLocation'],
          },
          {
            id: 'racing-professional-group',
            format: 'default',
            questions: ['racingProfessionalCash', 'racingTrackDetails'],
          },
          {
            id: 'racing-details-group',
            format: 'default',
            questions: ['racingYesAnswerDetails'],
          },
          {
            id: 'skydiving-engagement-group',
            title: '2. Skydiving',
            format: 'default',
            questions: ['skydivingEngagement'],
          },
          {
            id: 'skydiving-club-group',
            format: 'default',
            questions: ['skydivingClubMember'],
          },
          {
            id: 'skydiving-club-group-related',
            format: 'grid',
            questions: ['skydivingClubName', 'skydivingMemberPCA'],
          },
          {
            id: 'skydiving-experience-group',
            format: 'grid',
            questions: ['skydivingTimeEngaged', 'skydivingFormalTraining'],
          },
          {
            id: 'skydiving-jumps-group',
            format: 'grid',
            questions: [
              'skydivingTotalJumps',
              'skydivingJumpsLast12Months',
              'skydivingLastJumpDate',
              'skydivingJumpsNext12Months',
            ],
          },
          {
            id: 'skydiving-sanction-group',
            format: 'default',
            questions: ['skydivingClubSanction'],
          },
          {
            id: 'skydiving-professional-group',
            format: 'default',
            questions: ['skydivingProfessionalJumping', 'skydivingDetails'],
          },
          {
            id: 'skin-diving-engagement-group',
            title: '3. Skin or Scuba Diving',
            format: 'default',
            questions: ['skinDivingEngagement'],
          },
          {
            id: 'skin-diving-experience-group',
            format: 'grid',
            questions: ['skinDivingDuration', 'skinDivingLastDive'],
          },
          {
            id: 'skin-diving-training-group',
            format: 'grid',
            questions: ['skinDivingInstructions', 'skinDivingTotalHours', 'skinDivingFormalCourse'],
          },
          {
            id: 'skin-diving-equipment-group',
            format: 'default',
            questions: ['skinDivingEquipment'],
          },
          {
            id: 'skin-diving-club-group',
            format: 'grid',
            questions: ['skinDivingScubaClub', 'skinDivingClubName'],
          },
          {
            id: 'skin-diving-practice-group',
            format: 'default',
            questions: ['skinDivingWithoutBuddy', 'skinDivingArea', 'skinDivingPurpose'],
          },
          {
            id: 'skin-diving-commercial-group',
            format: 'default',
            questions: ['skinDivingForPay', 'skinDivingForPayExplain', 'skinDivingTreasureHunting'],
          },
          {
            id: 'skin-diving-depths-group',
            format: 'default',
            questions: ['skinDivingDepthsTable'],
          },
          {
            id: 'skin-diving-technical-group',
            format: 'grid',
            questions: [
              'skinDivingTanksUsed',
              'skinDivingCompressedAir',
              'skinDivingGreatestDepth',
            ],
          },
          {
            id: 'skin-diving-advisory-group',
            format: 'grid',
            questions: ['skinDivingAdvisedBeyond50', 'skinDivingAdvisedBeyond40'],
          },
          {
            id: 'skin-diving-advisory-explain-group',
            format: 'default',
            questions: ['skinDivingAdvisedExplain'],
          },
          {
            id: 'skin-diving-remarks-group',
            format: 'default',
            questions: ['skinDivingRemarks'],
          },
          {
            id: 'rodeo-association-group',
            title: '4. Rodeo Riding',
            format: 'default',
            questions: ['rodeoAssociation', 'rodeoOrganizationName'],
          },
          {
            id: 'rodeo-events-group',
            format: 'default',
            questions: ['rodeoEventTypes', 'rodeoEventOtherSpecify'],
          },
          {
            id: 'rodeo-participation-group',
            format: 'default',
            questions: ['rodeoParticipationFrequency', 'rodeoLocationsLastYear'],
          },
          {
            id: 'other-avocations-engagement-group',
            title: '5. Other Avocations',
            format: 'default',
            questions: ['otherAvocationsEngagement'],
          },
          {
            id: 'other-avocations-details-group',
            format: 'grid',
            questions: [
              'otherAvocationsType',
              'otherAvocationsFrequency',
              'otherAvocationsLastTime',
            ],
          },
          {
            id: 'other-avocations-remarks-group',
            format: 'default',
            questions: ['otherAvocationsRemarks'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          { field: 'medicalHazardousActivitiesFirstInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalHazardousActivitiesSecondInsured', operator: 'equals', value: 'yes' },
        ],
      },
    },
  },

  /* 31 ▸ Foreign Nationals or Foreign Travel Supplement ------------------------------------------------------- */
  foreignNationalsTravelSupplement: {
    id: 'foreign-nationals-travel-supplement',
    title: 'Foreign Nationals or Foreign Travel Supplement',
    subsections: [
      /* Q1: U.S. Citizenship Status */
      {
        id: 'foreign-nationals-travel-supplement-questions',
        title: 'Foreign Nationals or Foreign Travel Supplement',
        questionGroups: [
          {
            id: 'foreign-citizenship-group',
            title: '1. Citizenship Status',
            format: 'default',
            questions: ['foreignUSCitizen'],
          },
          {
            id: 'foreign-non-citizen-details-group',
            format: 'default',
            questions: [
              'foreignCountryCitizenship',
              'foreignAlienRegistrationNumber',
              'foreignVisaType',
            ],
          },
          {
            id: 'foreign-residency-status-group',
            format: 'grid',
            questions: ['foreignPermanentResident', 'foreignPermanentResidentDuration'],
          },
          {
            id: 'foreign-property-ownership-group',
            format: 'default',
            questions: [
              'foreignOwnHomeUS',
              'foreignOwnHomeUSAddress',
              'foreignOwnHomeForeignCountry',
              'foreignFamilyLiveWithYou',
            ],
          },
          {
            id: 'foreign-travel-plans-group',
            title: '2. Future Travel Plans',
            format: 'default',
            questions: ['foreignTravelPlans'],
          },
          {
            id: 'foreign-travel-details-group',
            format: 'grid',
            questions: [
              'foreignTravelWhere',
              'foreignTravelPurpose',
              'foreignTravelFrequency',
              'foreignTravelAveragePeriod',
            ],
          },
          {
            id: 'foreign-past-travel-group',
            title: '3. Past Travel History',
            format: 'default',
            questions: ['foreignPastTravelList'],
          },
          {
            id: 'foreign-occupational-duties-group',
            title: '4. Occupational Duties Outside United States',
            format: 'default',
            questions: ['foreignOccupationalDuties'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          { field: 'medicalForeignTravelFirstInsured', operator: 'equals', value: 'yes' },
          { field: 'medicalForeignTravelSecondInsured', operator: 'equals', value: 'yes' },
        ],
      },
    },
  },

  /* 32 ▸ Financial Institution Client Disclosure and Acknowledgment ------------------------------------------------------- */
  financialInstitutionDisclosure: {
    id: 'financial-institution-disclosure',
    title: 'Financial Institution Client Disclosure and Acknowledgment',
    subsections: [
      /* Client Information */
      {
        id: 'fi-disclosure-info',
        title: 'Financial Institution Client Disclosure and Acknowledgment',
        questionGroups: [
          {
            id: 'fi-disclosure-client-info-group',
            format: 'default',
            questions: ['fiDisclosureClientName'],
          },
          {
            id: 'fi-disclosure-company-group',
            title: 'I/we acknowledge the following',
            format: 'default',
            questions: ['fiDisclosureCompanySelection'],
          },
          {
            id: 'fi-disclosure-bank-info-group',
            title: `Hereinafter referred to as “the Company.” \n\nThe Company is an insurance company and is not a bank/credit union nor affiliated with any bank/credit union. Insurance and annuities that are purchased through the Company:`,
            description: `- are not insured by the Federal Deposit Insurance Corporation (FDIC), the National Credit Union Association (NCUA), the National Credit Union Share Insurance Fund (NCUSIF) or any other federal entity.
- are not deposits, or other obligations of and are not guaranteed by the bank/credit union.
- are not bank products and are not obligations of the **bank/credit union (Provide name of bank/credit union below)**.
- may lose value and are subject to investment risks, including the possible loss of the principal amount invested.
\n\n
Any insurance product guarantee is based upon the continued claims paying ability of the issuing insurance company and/or the ability of the insurance company to meet its financial obligations. \n\n
Insurance products are unrelated to and are not a condition of the provision or term of any banking or credit union service or activity.`,
            format: 'default',
            questions: ['fiDisclosureBankCreditUnionName'],
          },
        ],
      },
    ],
  },

  /* 33 ▸ Assignment for 1035 Exchange of Life Insurance or Annuity ------------------------------------------------------- */
  assignment1035Exchange: {
    id: 'assignment-1035-exchange',
    title: 'Assignment for 1035 Exchange of Life Insurance or Annuity',
    subsections: [
      /* Current Policy Information */
      {
        id: 'exchange-current-policy-info',
        title: 'Current Policy Information',
        questionGroups: [
          {
            id: 'exchange-current-insurer-group',
            format: 'default',
            questions: ['exchangeCurrentInsurer'],
          },
          {
            id: 'exchange-current-insurer-details-group',
            format: 'grid',
            questions: ['exchangeCurrentInsurerAddress', 'exchangeCurrentInsurerPhone'],
          },
          {
            id: 'exchange-policy-details-group',
            format: 'grid',
            questions: ['exchangeExistingPolicyNumbers', 'exchangeInsuredAnnuitantName'],
          },
          {
            id: 'exchange-owner-details-group',
            format: 'grid',
            questions: ['exchangeOwnerName', 'exchangeOwnerSSN'],
          },
        ],
      },

      /* Company Selection */
      {
        id: 'exchange-company-selection-section',
        title: 'Company Selection',
        questionGroups: [
          {
            id: 'exchange-company-selection-group',
            format: 'default',
            questions: ['exchangeCompanySelection'],
          },
        ],
      },

      /* Assignment Type */
      {
        id: 'exchange-assignment-type',
        title: 'Assignment Type',
        questionGroups: [
          {
            id: 'exchange-type-group',
            format: 'default',
            questions: ['exchangeType', 'exchangePartialAmount', 'exchangePartialPercentage'],
          },
        ],
      },

      /* Policy Requirements */
      {
        id: 'exchange-policy-requirements',
        title: 'Policy Requirements for Full Surrenders',
        questionGroups: [
          {
            id: 'exchange-surrender-reason-group',
            format: 'default',
            questions: ['exchangeSurrenderReason'],
          },
        ],
      },

      /* Payment Details */
      {
        id: 'exchange-payment-details',
        title: 'Payment Details',
        questionGroups: [
          {
            id: 'exchange-payment-company-group',
            format: 'default',
            questions: ['exchangePaymentCompany'],
          },
        ],
      },

      // issued different than applied
      {
        id: 'exchange-issued-different-than-applied',
        title: 'Issued Different Than Applied',
        questionGroups: [
          {
            id: 'exchange-issued-different-than-applied-group',
            format: 'default',
            questions: [
              'exchangeIssuedDifferentThanApplied',
              'authorizeTransferFundsIssuedDifferentThanApplied',
            ],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          { field: 'paymentMethod', operator: 'equals', value: 'full_1035' },
          { field: 'paymentMethod', operator: 'equals', value: 'partial_1035' },
        ],
      },
    },
  },

  /* 34 ▸ Acceptance of §1035 Exchange - Issued different than applied ------------------------------------------------------- */
  acceptance1035Exchange: {
    id: 'acceptance-1035-exchange',
    title: 'Acceptance of §1035 Exchange - Issued different than applied',
    subsections: [
      /* Proposed Policy Information */
      {
        id: 'exchange-1035-proposed-policy',
        title: 'Proposed Policy Information',
        questionGroups: [
          {
            id: 'exchange-1035-proposed-policy-group',
            format: 'default',
            questions: ['exchange1035ProposedPolicyholder', 'exchange1035ProposedPolicyNumber'],
          },
        ],
      },

      /* Company Selection */
      {
        id: 'exchange-1035-company-section',
        title: 'Company Selection',
        questionGroups: [
          {
            id: 'exchange-1035-company-group',
            format: 'default',
            questions: ['exchange1035Company'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        field: 'authorizeTransferFundsIssuedDifferentThanApplied',
        operator: 'equals',
        value: 'yes',
      },
    },
  },

  /* 35 ▸ Section 1035 Partial Transfer Request ------------------------------------------------------- */
  section1035PartialTransfer: {
    id: 'section-1035-partial-transfer',
    title: 'Section 1035 Partial Transfer Request',
    subsections: [
      /* Section 1 - Owner Information */
      {
        id: 'partial-1035-owner-info',
        title: '1. Owner Information',
        questionGroups: [
          {
            id: 'partial-1035-owner-name-ssn-group',
            format: 'grid',
            questions: ['partial1035OwnerName', 'partial1035OwnerSSN'],
          },
          {
            id: 'partial-1035-owner-address-group',
            format: 'default',
            questions: ['partial1035OwnerAddress'],
          },
          {
            id: 'partial-1035-owner-location-group',
            format: 'grid',
            questions: ['partial1035OwnerCity', 'partial1035OwnerState', 'partial1035OwnerZipCode'],
          },
          {
            id: 'partial-1035-owner-phone-group',
            format: 'grid',
            questions: ['partial1035DaytimePhone', 'partial1035EveningPhone'],
          },
        ],
      },

      /* Section 2 - Amounts to Be Liquidated and Transferred */
      {
        id: 'partial-1035-transfer-amounts',
        title: '2. Amounts to Be Liquidated and Transferred to State Life',
        questionGroups: [
          {
            id: 'partial-1035-insurance-company-group',
            format: 'default',
            questions: ['partial1035PresentInsuranceCompany'],
          },
          {
            id: 'partial-1035-transfer-details-group',
            format: 'default',
            questions: ['partial1035TransferDetails'],
          },
          {
            id: 'partial-1035-company-address-group',
            format: 'default',
            questions: ['partial1035CompanyAddress'],
          },
          {
            id: 'partial-1035-company-location-group',
            format: 'grid',
            questions: [
              'partial1035CompanyCity',
              'partial1035CompanyState',
              'partial1035CompanyZipCode',
            ],
          },
          {
            id: 'partial-1035-process-timing-group',
            format: 'grid',
            questions: ['partial1035ProcessRequest', 'partial1035ProcessDate'],
          },
        ],
      },

      /* Section 3 - Partial Transfer Authorization */
      {
        id: 'partial-1035-authorization',
        title: '3. Partial Transfer Authorization and Instructions',
        questionGroups: [
          {
            id: 'partial-1035-authorization-group',
            format: 'default',
            questions: [], // This section contains authorization text only
          },
        ],
      },
    ],
    conditional: {
      visible: {
        operator: 'and',
        conditions: [
          { field: 'paymentMethodAlternative', operator: 'equals', value: 'yes' },
          { field: 'paymentMethod', operator: 'equals', value: 'partial_1035' },
        ],
      },
    },
  },

  /* 36 ▸ Power of Attorney Affidavit ------------------------------------------------------- */
  powerOfAttorneyAffidavit: {
    id: 'power-of-attorney-affidavit',
    title: 'Power of Attorney Affidavit',
    subsections: [
      /* Company and Policy Information */
      {
        id: 'poa-affidavit-company-policy-info',
        title: 'Power of Attorney Affidavit',
        questionGroups: [
          {
            id: 'poa-affidavit-initial-confirmation-group',
            format: 'default',
            questions: ['poaAffidavitInitialConfirmation'],
          },
          {
            id: 'poa-affidavit-company-selection-group',
            format: 'default',
            questions: ['poaAffidavitCompanySelection'],
          },
          {
            id: 'poa-affidavit-policy-insured-group',
            format: 'grid',
            questions: ['poaAffidavitPolicyAccountNumber', 'poaAffidavitInsuredAccountholderName'],
          },
          {
            id: 'poa-affidavit-location-group',
            format: 'grid',
            questions: ['poaAffidavitState', 'poaAffidavitCounty'],
          },
          {
            id: 'poa-affidavit-parties-group',
            format: 'grid',
            title:
              '(“Affiant”) being first duly sworn upon his/her oath, does state that Affiant is the agent for  (“Principal”) under a Power of Attorney.',
            questions: [
              'poaAffidavitAffiantName',
              'poaAffidavitPrincipalName',
              'poaAffidavitPowerOfAttorneyDate',
            ],
          },
          {
            id: 'poa-affidavit-notary-confirmation-group',
            title: 'Notary Acknowledgment',
            format: 'default',
            questions: ['poaAffidavitNotaryInitialConfirmation'],
          },
          {
            id: 'poa-affidavit-notary-info-group',
            title: 'Notary Acknowledgment',
            format: 'grid',
            questions: [
              'poaAffidavitNotaryState',
              'poaAffidavitNotaryCounty',
              'poaAffidavitNotaryAppearedName',
            ],
          },
          {
            id: 'poa-affidavit-notary-appeared-group',
            format: 'grid',
            questions: [
              'poaAffidavitNotaryCommissionExpires',
              'poaAffidavitNotaryCountyOfResidence',
            ],
          },
        ],
      },
    ],
  },

  /* 37 ▸ Signature Authority of Entity ------------------------------------------------------- */
  signatureAuthorityEntity: {
    id: 'signature-authority-entity',
    title: 'Signature Authority of Entity',
    subsections: [
      /* Policy and Authorization Information */
      {
        id: 'signature-authority-policy-info',
        title: 'Policy and Authorization Information',
        questionGroups: [
          {
            id: 'signature-authority-policy-group',
            format: 'default',
            questions: ['signatureAuthorityPolicyNumber'],
          },
          {
            id: 'signature-authority-type-group',
            format: 'default',
            questions: ['signatureAuthorityType'],
          },
        ],
      },

      /* Entity Information */
      {
        id: 'signature-authority-entity-info',
        title: 'Entity Information',
        questionGroups: [
          {
            id: 'signature-authority-entity-details-group',
            format: 'default',
            questions: ['signatureAuthorityEntityName'],
          },
          {
            id: 'signature-authority-entity-type-group',
            format: 'default',
            questions: ['signatureAuthorityEntityType'],
          },
          {
            id: 'signature-authority-tax-id-group',
            format: 'default',
            questions: ['signatureAuthorityTaxId'],
          },
        ],
      },

      /* Authorizing Resolution */
      {
        id: 'signature-authority-resolution',
        title: 'Authorizing Resolution',
        questionGroups: [
          {
            id: 'signature-authority-resolution-details-group',
            format: 'grid',
            questions: ['signatureAuthoritySecretaryName', 'signatureAuthorityCompanyName'],
          },
          {
            id: 'signature-authority-meeting-details-group',
            format: 'grid',
            questions: ['signatureAuthorityState', 'signatureAuthorityMeetingDate'],
          },
          {
            id: 'signature-authority-individuals-group',
            format: 'default',
            questions: ['signatureAuthorityAuthorizedIndividuals'],
          },
        ],
      },
    ],
    conditional: {
      visible: { field: 'policyOwners', operator: 'equals', value: 'corporation' },
    },
  },

  /* 39 ▸ W-4P Withholding Certificate for Periodic Pension or Annuity Payments ------------------------------------------------------- */
  w4p: {
    id: 'w4p',
    title: 'Withholding Certificate for Periodic Pension or Annuity Payments',
    conditional: {
      visible: {
        operator: 'or',
        conditions: [
          {
            field: 'paymentMethod',
            operator: 'equals',
            value: 'annuity',
          },
          { field: 'policyProductFundingSelection', operator: 'equals', value: 'annuity-funding' },
        ],
      },
    },
    subsections: [
      {
        id: 'step-1-questions',
        title: 'Personal Information',
        questionGroups: [
          {
            id: 'step-1-personal-information-questions-a',
            format: 'grid',
            className: 'grid-cols-3',
            questions: [
              'w4pPersonalInfoFirstName',
              'w4pPersonalInfoLastName',
              'w4pPersonalInfoSSN',
            ],
          },
          {
            id: 'step-1-personal-information-questions-b',
            format: 'grid',
            questions: ['w4pPersonalInfoAddress', 'w4pPersonalInfoCityStateZip'],
          },
          {
            id: 'step-1-personal-information-questions-c',
            format: 'default',
            questions: ['w4pPersonalInfoFilingStatus'],
          },
        ],
      },
      {
        id: 'step-2-questions',
        title:
          "Income From a Job and/or Multiple Pensions/ Annuities (Including a Spouse'sJob/Pension/Annuity)",
        questionGroups: [
          {
            id: 'step-2-income-questions',
            format: 'default',
            questions: [
              'w4pAnnualIncomeJobs',
              'w4pAnnualIncomeOtherPensions',
              'w4pStep3DependentsUnder17',
            ],
          },
        ],
      },
      {
        id: 'step-3-questions',
        title: 'Claim Dependent and Other Credits',
        questionGroups: [
          {
            id: 'step-3-claim-questions',
            format: 'default',
            questions: [
              'w4pStep3OtherChildren',
              'w4pStep3OtherDependents',
              'w4pStep3OtherCredits',
              'w4pStep3TotalCredits',
            ],
          },
        ],
      },
      {
        id: 'step-4-questions',
        title: 'Other Adjustments',
        questionGroups: [
          {
            id: 'step-4-other-adjustments-questions',
            format: 'default',
            questions: ['w4pStep4OtherIncome', 'w4pStep4Deductions', 'w4pStep4ExtraWithholding'],
          },
        ],
      },
      {
        id: 'step-4b-questions',
        title: 'Deductions Worksheet',
        questionGroups: [
          {
            id: 'step-4b-deductions-questions',
            format: 'default',
            questions: [
              'deductionsWorksheetLine1Estimate',
              'deductionsWorksheetLine2StandardDeduction',
              'deductionsWorksheetLine3Difference',
              'deductionsWorksheetLine4AgeAdjustment',
              'deductionsWorksheetLine5Adjustments',
              'deductionsWorksheetLine6Total',
            ],
          },
        ],
      },
    ],
  },

  /* 40 ▸ Pre-interview Worksheet ------------------------------------------------------- */
  preInterviewWorksheet: {
    id: 'pre-interview-worksheet',
    title: 'Pre-interview Worksheet',
    conditional: {
      visible: {
        field: 'healthPreQuestion',
        operator: 'equals',
        value: 'complete-tele-interview',
      },
    },
    subsections: [
      {
        id: 'pre-interview-worksheet-questions',
        title: 'Pre-interview Worksheet',
        questionGroups: [
          {
            id: 'pre-interview-worksheet-questions-group',
            format: 'default',
            questions: ['doctorsVisited', 'prescriptionMedications', 'medicalHistory'],
          },
        ],
      },
    ],
  },
};
